import React, { useState } from 'react';
import styled from 'styled-components';
import { ExternalLink, Play, Film, Users, Link2, ChevronDown, ChevronUp, ListVideo } from 'lucide-react';
import VideoModal, { youtubeThumbnailUrl } from './VideoModal';
import TvSeasonsModal from './TvSeasonsModal';

const Section = styled.section`
  margin-top: 2rem;
`;

const SectionHeading = styled.h2`
  font-size: ${({ theme }) => theme.fontClamp?.xl || '1.25rem'};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0 0 0.75rem;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  padding: 1.5rem;
`;

const FactsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.25rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const FactItem = styled.div`
  min-width: 0;
`;

const FactListBlock = styled.div`
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  min-width: 0;
`;

const FactLabel = styled.p`
  margin: 0 0 0.45rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const FactValue = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  line-height: 1.4;
  word-break: break-word;
`;

const FactChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const FactChip = styled.span`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: rgba(0, 48, 135, 0.06);
  border: 1px solid rgba(0, 48, 135, 0.12);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.foreground};
  word-break: break-word;
`;

const SeasonsBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.65rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radiusSm || '10px'};
  background: rgba(0, 48, 135, 0.06);
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, transform 0.15s;

  &:hover {
    background: rgba(0, 48, 135, 0.12);
    transform: translateY(-1px);
  }
`;

const FactToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.65rem;
  padding: 0;
  border: none;
  background: none;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ProviderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;

const ProviderChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.65rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: 0.8125rem;
  font-weight: 500;
  img {
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 6px;
    object-fit: cover;
  }
`;

const TrailerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
`;

const TrailerCard = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radiusSm};
  overflow: hidden;
  text-align: left;
  background: ${({ theme }) => theme.colors.card};
  color: inherit;
  cursor: pointer;
  transition: border-color 0.2s, transform 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const TrailerThumb = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  background-color: ${({ theme }) => theme.colors.muted};
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
  background-position: center;
`;

const TrailerPlayOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.28);
  color: #fff;
  transition: background 0.2s;

  ${TrailerCard}:hover & {
    background: rgba(0, 0, 0, 0.4);
  }
`;

const PlayBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  border: 2px solid rgba(255, 255, 255, 0.85);
`;

const TrailerTitle = styled.p`
  margin: 0;
  padding: 0.65rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
`;

const CastGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 1rem;
`;

const CastCard = styled.div`
  text-align: center;
`;

const CastPhoto = styled.div`
  width: 72px;
  height: 72px;
  margin: 0 auto 0.5rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.muted};
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const CastName = styled.p`
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.3;
`;

const CastRole = styled.p`
  margin: 0.15rem 0 0;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  line-height: 1.3;
`;

const CrewList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.35rem;
  font-size: 0.875rem;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  padding: 0.35rem 0.75rem;
  background: rgba(220, 38, 38, 0.08);
  color: ${({ theme }) => theme.colors.crimson};
  font-size: 0.8125rem;
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const ExternalLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const ExtLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const CollectionBanner = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  img {
    width: 4rem;
    height: 4rem;
    border-radius: ${({ theme }) => theme.radiusSm};
    object-fit: cover;
  }
`;

const Disclaimer = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

function formatRuntime(minutes) {
  const m = Number(minutes);
  if (!Number.isFinite(m) || m <= 0) return null;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h > 0 && r > 0) return `${h}h ${r}m`;
  if (h > 0) return `${h}h`;
  return `${r}m`;
}

function formatMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return null;
  if (v >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(1)}B`;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  return `$${v.toLocaleString()}`;
}

function ProviderGroup({ label, items }) {
  if (!items?.length) return null;
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <FactLabel>{label}</FactLabel>
      <ProviderRow>
        {items.map((p) => (
          <ProviderChip key={`${label}-${p.id}`}>
            {p.logoUrl ? <img src={p.logoUrl} alt="" /> : null}
            {p.name}
          </ProviderChip>
        ))}
      </ProviderRow>
    </div>
  );
}

function CollapsibleChipList({ items, collapseThreshold = 10, t }) {
  const [expanded, setExpanded] = useState(false);
  if (!items?.length) return null;

  const needsCollapse = items.length > collapseThreshold;
  const visible = expanded || !needsCollapse ? items : items.slice(0, collapseThreshold);

  return (
    <>
      <FactChipRow>
        {visible.map((item) => (
          <FactChip key={item}>{item}</FactChip>
        ))}
      </FactChipRow>
      {needsCollapse ? (
        <FactToggle type="button" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
          {expanded ? (
            <>
              {t('common.showLess', 'Show less')} <ChevronUp size={14} />
            </>
          ) : (
            <>
              {t('media.detail.showAll', 'Show all')} ({items.length}) <ChevronDown size={14} />
            </>
          )}
        </FactToggle>
      ) : null}
    </>
  );
}

function buildQuickFacts(hero, isMovie, t) {
  const runtimeLabel = formatRuntime(hero.runtime || hero.episodeRunTime);
  const compact = [
    hero.status ? { label: t('media.detail.status', 'Status'), value: hero.status } : null,
    runtimeLabel ? { label: t('media.detail.runtime', 'Runtime'), value: runtimeLabel } : null,
    !isMovie && hero.numberOfSeasons
      ? { label: t('media.detail.seasons', 'Seasons'), value: String(hero.numberOfSeasons) }
      : null,
    !isMovie && hero.numberOfEpisodes
      ? { label: t('media.detail.episodes', 'Episodes'), value: String(hero.numberOfEpisodes) }
      : null,
    hero.certification
      ? { label: t('media.detail.certification', 'Rating'), value: hero.certification }
      : null,
    isMovie && hero.budget ? { label: t('media.detail.budget', 'Budget'), value: formatMoney(hero.budget) } : null,
    isMovie && hero.revenue ? { label: t('media.detail.revenue', 'Revenue'), value: formatMoney(hero.revenue) } : null,
  ].filter(Boolean);

  const lists = [
    hero.spokenLanguages?.length
      ? {
          label: t('media.detail.languages', 'Languages'),
          items: hero.spokenLanguages,
          collapseThreshold: 8,
        }
      : null,
    hero.productionCountries?.length
      ? {
          label: t('media.detail.countries', 'Countries'),
          items: hero.productionCountries,
          collapseThreshold: 8,
        }
      : null,
    !isMovie && hero.networks?.length
      ? {
          label: t('media.detail.network', 'Network'),
          items: hero.networks,
          collapseThreshold: 10,
        }
      : null,
    !isMovie && hero.createdBy?.length
      ? {
          label: t('media.detail.createdBy', 'Created by'),
          items: hero.createdBy,
          collapseThreshold: 6,
        }
      : null,
  ].filter(Boolean);

  return { compact, lists };
}

export default function MediaDetailExtras({ hero, category, t, mediaId, language, locale, showTitle }) {
  const [activeVideo, setActiveVideo] = useState(null);
  const [seasonsOpen, setSeasonsOpen] = useState(false);

  if (!hero) return null;

  const isMovie = category === 'movie' || category === 'anime_movie';
  const isTv = category === 'tv' || category === 'anime_tv';
  const showSeasonsBtn =
    isTv && (hero.numberOfSeasons > 0 || hero.numberOfEpisodes > 0);
  const { compact: compactFacts, lists: listFacts } = buildQuickFacts(hero, isMovie, t);

  const trailers = [...(hero.videos?.trailers || []), ...(hero.videos?.teasers || [])].slice(0, 6);
  const cast = hero.credits?.cast || [];
  const crew = hero.credits?.crew || [];
  const keywords = hero.keywords || [];
  const providers = hero.watchProviders;
  const hasProviders =
    providers &&
    (providers.flatrate?.length || providers.rent?.length || providers.buy?.length);
  const imdbId = hero.externalIds?.imdbId;
  const homepage = hero.homepage;

  const hasFacts = compactFacts.length > 0 || listFacts.length > 0;
  const hasExtras =
    hasFacts ||
    showSeasonsBtn ||
    hasProviders ||
    trailers.length ||
    cast.length ||
    crew.length ||
    keywords.length;

  if (!hasExtras && !hero.belongsToCollection && !imdbId && !homepage) return null;

  return (
    <>
      {hasFacts ? (
        <Section>
          <SectionHeading>{t('media.detail.quickFacts', 'Quick facts')}</SectionHeading>
          <Card>
            {compactFacts.length > 0 ? (
              <FactsGrid>
                {compactFacts.map((f) => (
                  <FactItem key={f.label}>
                    <FactLabel>{f.label}</FactLabel>
                    <FactValue>{f.value}</FactValue>
                  </FactItem>
                ))}
              </FactsGrid>
            ) : null}
            {listFacts.map((f) => (
              <FactListBlock key={f.label}>
                <FactLabel>{f.label}</FactLabel>
                <CollapsibleChipList
                  items={f.items}
                  collapseThreshold={f.collapseThreshold}
                  t={t}
                />
              </FactListBlock>
            ))}
            {showSeasonsBtn ? (
              <SeasonsBtn type="button" onClick={() => setSeasonsOpen(true)}>
                <ListVideo size={18} />
                {t('media.detail.viewSeasonsEpisodes', 'View all seasons & episodes')}
              </SeasonsBtn>
            ) : null}
          </Card>
        </Section>
      ) : showSeasonsBtn ? (
        <Section>
          <SectionHeading>{t('media.detail.seasonsEpisodes', 'Seasons & episodes')}</SectionHeading>
          <Card>
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>
              {hero.numberOfSeasons
                ? `${hero.numberOfSeasons} ${t('media.detail.seasons', 'Seasons').toLowerCase()}`
                : ''}
              {hero.numberOfSeasons && hero.numberOfEpisodes ? ' · ' : ''}
              {hero.numberOfEpisodes
                ? `${hero.numberOfEpisodes} ${t('media.detail.episodes', 'Episodes').toLowerCase()}`
                : ''}
            </p>
            <SeasonsBtn type="button" onClick={() => setSeasonsOpen(true)}>
              <ListVideo size={18} />
              {t('media.detail.viewSeasonsEpisodes', 'View all seasons & episodes')}
            </SeasonsBtn>
          </Card>
        </Section>
      ) : null}

      {hasProviders ? (
        <Section>
          <SectionHeading>{t('media.detail.whereToWatch', 'Where to watch')}</SectionHeading>
          <Card>
            <ProviderGroup label={t('media.detail.stream', 'Stream')} items={providers.flatrate} />
            <ProviderGroup label={t('media.detail.rent', 'Rent')} items={providers.rent} />
            <ProviderGroup label={t('media.detail.buy', 'Buy')} items={providers.buy} />
            {providers.link ? (
              <ExtLink href={providers.link} target="_blank" rel="noopener noreferrer">
                {t('media.detail.viewAllProviders', 'View all providers')} <ExternalLink size={14} />
              </ExtLink>
            ) : null}
            <Disclaimer>
              {t(
                'media.detail.providerDisclaimer',
                'Availability may vary by region. Data provided by TMDB.',
              )}
            </Disclaimer>
          </Card>
        </Section>
      ) : null}

      {trailers.length > 0 ? (
        <Section>
          <SectionHeading>{t('media.detail.trailers', 'Trailers')}</SectionHeading>
          <TrailerGrid>
            {trailers.map((v) => {
              const thumbUrl = youtubeThumbnailUrl(v.key);
              return (
                <TrailerCard
                  key={v.id || v.key}
                  type="button"
                  onClick={() => setActiveVideo(v)}
                  aria-label={v.name || t('media.detail.trailer', 'Trailer')}
                >
                  <TrailerThumb $url={thumbUrl}>
                    <TrailerPlayOverlay aria-hidden>
                      <PlayBadge>
                        <Play size={22} fill="currentColor" />
                      </PlayBadge>
                    </TrailerPlayOverlay>
                  </TrailerThumb>
                  <TrailerTitle>{v.name || t('media.detail.trailer', 'Trailer')}</TrailerTitle>
                </TrailerCard>
              );
            })}
          </TrailerGrid>
          <VideoModal
            open={Boolean(activeVideo?.key)}
            videoKey={activeVideo?.key}
            title={activeVideo?.name}
            onClose={() => setActiveVideo(null)}
          />
        </Section>
      ) : null}

      {cast.length > 0 ? (
        <Section>
          <SectionHeading>
            <Users size={18} style={{ verticalAlign: 'middle', marginRight: '0.35rem' }} />
            {t('media.detail.cast', 'Cast')}
          </SectionHeading>
          <CastGrid>
            {cast.map((c) => (
              <CastCard key={c.id}>
                <CastPhoto $url={c.profileUrl} aria-hidden>
                  {!c.profileUrl ? <Users size={20} /> : null}
                </CastPhoto>
                <CastName>{c.name}</CastName>
                {c.character ? <CastRole>{c.character}</CastRole> : null}
              </CastCard>
            ))}
          </CastGrid>
        </Section>
      ) : null}

      {crew.length > 0 ? (
        <Section>
          <SectionHeading>{t('media.detail.crew', 'Key crew')}</SectionHeading>
          <Card>
            <CrewList>
              {crew.map((c) => (
                <li key={`${c.id}-${c.job}`}>
                  <strong>{c.name}</strong> — {c.job}
                </li>
              ))}
            </CrewList>
          </Card>
        </Section>
      ) : null}

      {keywords.length > 0 ? (
        <Section>
          <SectionHeading>{t('media.detail.keywords', 'Keywords')}</SectionHeading>
          <TagRow>
            {keywords.map((k) => (
              <Tag key={k.id}>{k.name}</Tag>
            ))}
          </TagRow>
        </Section>
      ) : null}

      {hero.belongsToCollection ? (
        <Section>
          <SectionHeading>
            <Film size={18} style={{ verticalAlign: 'middle', marginRight: '0.35rem' }} />
            {t('media.detail.collection', 'Collection')}
          </SectionHeading>
          <Card>
            <CollectionBanner>
              {hero.belongsToCollection.posterUrl ? (
                <img src={hero.belongsToCollection.posterUrl} alt="" />
              ) : null}
              <FactValue>{hero.belongsToCollection.name}</FactValue>
            </CollectionBanner>
          </Card>
        </Section>
      ) : null}

      {imdbId || homepage ? (
        <Section>
          <SectionHeading>
            <Link2 size={18} style={{ verticalAlign: 'middle', marginRight: '0.35rem' }} />
            {t('media.detail.externalLinks', 'External links')}
          </SectionHeading>
          <ExternalLinks>
            {imdbId ? (
              <ExtLink href={`https://www.imdb.com/title/${imdbId}/`} target="_blank" rel="noopener noreferrer">
                IMDb <ExternalLink size={14} />
              </ExtLink>
            ) : null}
            {homepage ? (
              <ExtLink href={homepage} target="_blank" rel="noopener noreferrer">
                {t('media.detail.officialSite', 'Official site')} <ExternalLink size={14} />
              </ExtLink>
            ) : null}
          </ExternalLinks>
        </Section>
      ) : null}

      <TvSeasonsModal
        open={seasonsOpen}
        onClose={() => setSeasonsOpen(false)}
        category={category}
        mediaId={mediaId}
        language={language}
        showTitle={showTitle}
        t={t}
        locale={locale}
      />
    </>
  );
}
